import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../components/atoms/property-list-item';
import { VisitState } from '../../api/types/visits';

import { Can } from '@/permissions/can';

interface Props {
  visitState: VisitState;
  createdAt: string | Date;
  expectedAt: string;
  arrivedAt: string;
  finishesAt: string;
  handleOpenExtendVisitDialog: () => void;
}

export const VisitHours = ({
  arrivedAt,
  createdAt,
  expectedAt,
  finishesAt,
  visitState,
  handleOpenExtendVisitDialog,
}: Props) => {
  const { t } = useTranslation(['visit', 'common']);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const align = mdDown ? 'vertical' : 'horizontal';

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Typography>{t('visit:hours.cardTitle')}</Typography>{' '}
            <Can I="extendVisit" a="Visitors" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleOpenExtendVisitDialog}
                      disabled={
                        visitState === VisitState.CANCELLED ||
                        visitState === VisitState.FINISHED ||
                        !allowed
                      }>
                      {t('visit:extendVisitDialog.title')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          </Box>
        }
      />
      <Divider />
      <PropertyList>
        <PropertyListItem
          align={align}
          label={t('visit:hours.createdAt')}
          value={createdAt ? format(new Date(createdAt), 'dd MMM yyyy HH:mm') : '-'}
        />
        <PropertyListItem
          align={align}
          label={t('visit:hours.expectedAt')}
          value={expectedAt ? format(new Date(expectedAt), 'dd MMM yyyy HH:mm') : '-'}
        />
        <PropertyListItem
          align={align}
          label={t('visit:hours.arrivedAt')}
          value={arrivedAt ? format(new Date(arrivedAt), 'dd MMM yyyy HH:mm') : '-'}
        />
        <PropertyListItem
          align={align}
          label={t('visit:hours.finishesAt')}
          value={finishesAt ? format(new Date(finishesAt), 'dd MMM yyyy HH:mm') : '-'}
        />
      </PropertyList>
    </Card>
  );
};
